import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Shield, Clock, Archive, CheckCircle2 } from "lucide-react";
import { generateWorkflowOnePager } from "@/services/workflowDocService";
import { toast } from "sonner";

const WorkflowDocs = () => {
  const navigate = useNavigate();

  const handleDownloadOnePager = () => {
    generateWorkflowOnePager("AthleteNexus");
    toast.success("One-pager downloaded successfully");
  };

  const slaData = [
    {
      dealType: "Standard Deals",
      value: "< $5,000",
      reviewTime: "48 hours",
      escalation: "72 hours",
      sla: "95%"
    },
    {
      dealType: "High-Value Deals",
      value: "$5,000 - $25,000",
      reviewTime: "72 hours",
      escalation: "96 hours",
      sla: "98%"
    },
    {
      dealType: "Premium Deals",
      value: "> $25,000",
      reviewTime: "96 hours",
      escalation: "120 hours",
      sla: "99%"
    },
    {
      dealType: "Critical Risk Deals",
      value: "Any amount",
      reviewTime: "4 hours",
      escalation: "Immediate",
      sla: "100%"
    }
  ];

  const rolePermissions = [
    {
      role: "Super Admin",
      dealSubmission: "✓",
      dealApproval: "✓",
      userManagement: "✓",
      analytics: "✓",
      policyConfig: "✓",
      auditAccess: "Full (7 years)"
    },
    {
      role: "Compliance Officer",
      dealSubmission: "✓",
      dealApproval: "✓",
      userManagement: "Limited",
      analytics: "✓",
      policyConfig: "✓",
      auditAccess: "Full (7 years)"
    },
    {
      role: "Athletic Director",
      dealSubmission: "✓",
      dealApproval: "Department only",
      userManagement: "−",
      analytics: "✓",
      policyConfig: "View only",
      auditAccess: "Department (7 years)"
    },
    {
      role: "Coach",
      dealSubmission: "✓",
      dealApproval: "−",
      userManagement: "−",
      analytics: "Team only",
      policyConfig: "−",
      auditAccess: "Limited (1 year)"
    }
  ];

  const retentionPolicies = [
    {
      dataType: "Active Deal Records",
      retention: "Indefinite",
      purpose: "Ongoing compliance monitoring"
    },
    {
      dataType: "Audit Logs",
      retention: "7 years",
      purpose: "SOC 2 / NCAA compliance requirements"
    },
    {
      dataType: "AI Analysis History",
      retention: "3 years",
      purpose: "Model improvement and verification"
    },
    {
      dataType: "Archived Deals",
      retention: "Permanent",
      purpose: "Historical compliance documentation"
    },
    {
      dataType: "User Activity Logs",
      retention: "2 years",
      purpose: "Security audit and investigation"
    },
    {
      dataType: "Financial Records",
      retention: "7 years",
      purpose: "IRS and financial audit compliance"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="text-sm font-light tracking-wide text-foreground hover:text-primary transition-colors"
          >
            ← AthleteNexus
          </button>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/demo-workflow")}
            >
              Demo Workflow
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDownloadOnePager}
            >
              <Download className="w-4 h-4 mr-2" />
              Download One-Pager
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-16 px-8 bg-muted/30">
        <div className="max-w-[1200px] mx-auto text-center">
          <Badge variant="secondary" className="mb-4">
            Documentation
          </Badge>
          <h1 className="text-5xl font-light leading-tight tracking-tight text-foreground mb-6">
            Workflow Documentation
          </h1>
          <p className="text-xl font-light text-muted-foreground max-w-[700px] mx-auto">
            Complete reference for SLAs, retention policies, role-based permissions, and compliance standards
          </p>
        </div>
      </section>

      {/* SLA Matrix */}
      <section className="py-16 px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Clock className="w-8 h-8 text-primary" />
            <h2 className="text-3xl font-light text-foreground">
              Service Level Agreements
            </h2>
          </div>
          
          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Deal Type</TableHead>
                  <TableHead>Deal Value</TableHead>
                  <TableHead>Review Time</TableHead>
                  <TableHead>Escalation Threshold</TableHead>
                  <TableHead>SLA Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slaData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{row.dealType}</TableCell>
                    <TableCell>{row.value}</TableCell>
                    <TableCell>{row.reviewTime}</TableCell>
                    <TableCell>{row.escalation}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.sla}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="mt-6 p-4 bg-muted/30 rounded-lg border border-border">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Uptime Guarantee
                </p>
                <p className="text-sm text-muted-foreground">
                  99.5% platform uptime guarantee with 24/7 monitoring and automated failover systems. System maintenance windows scheduled during low-traffic periods with 48-hour advance notice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Retention Policy */}
      <section className="py-16 px-8 bg-muted/30">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Archive className="w-8 h-8 text-primary" />
            <h2 className="text-3xl font-light text-foreground">
              Data Retention Policy
            </h2>
          </div>

          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data Type</TableHead>
                  <TableHead>Retention Period</TableHead>
                  <TableHead>Purpose</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {retentionPolicies.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{row.dataType}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{row.retention}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.purpose}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="mt-6 p-4 bg-card rounded-lg border border-border">
            <h3 className="text-sm font-medium text-foreground mb-3">
              Compliance Standards
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5" />
                <div>
                  <p className="text-sm font-medium">SOC 2 Type II</p>
                  <p className="text-xs text-muted-foreground">Annual audit compliance</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5" />
                <div>
                  <p className="text-sm font-medium">NCAA Standards</p>
                  <p className="text-xs text-muted-foreground">Full record retention</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-success mt-0.5" />
                <div>
                  <p className="text-sm font-medium">GDPR / CCPA</p>
                  <p className="text-xs text-muted-foreground">Data privacy compliance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Role-Based Permissions */}
      <section className="py-16 px-8">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-8 h-8 text-primary" />
            <h2 className="text-3xl font-light text-foreground">
              Role-Based Permissions Matrix
            </h2>
          </div>

          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Deal Submission</TableHead>
                  <TableHead>Deal Approval</TableHead>
                  <TableHead>User Management</TableHead>
                  <TableHead>Analytics</TableHead>
                  <TableHead>Policy Config</TableHead>
                  <TableHead>Audit Log Access</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rolePermissions.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{row.role}</TableCell>
                    <TableCell>
                      {row.dealSubmission === "✓" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <span className="text-muted-foreground">{row.dealSubmission}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.dealApproval === "✓" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.dealApproval}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.userManagement === "✓" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.userManagement}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.analytics === "✓" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.analytics}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.policyConfig === "✓" ? (
                        <CheckCircle2 className="w-4 h-4 text-success" />
                      ) : (
                        <span className="text-muted-foreground text-sm">{row.policyConfig}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.auditAccess}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h3 className="text-sm font-medium text-foreground mb-2">
                API Access Scopes
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Role-based API access controls for external integrations
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Super Admin: Full API access with write permissions</li>
                <li>• Compliance Officer: Read/write access to deals and analytics</li>
                <li>• Athletic Director: Read access to department data only</li>
                <li>• Coach: Read access to team athlete data only</li>
              </ul>
            </div>

            <div className="p-4 bg-muted/30 rounded-lg border border-border">
              <h3 className="text-sm font-medium text-foreground mb-2">
                Security Features
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Enterprise-grade security for all user roles
              </p>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Multi-factor authentication (MFA) required for admins</li>
                <li>• Session timeout after 30 minutes of inactivity</li>
                <li>• IP whitelisting available for institutional access</li>
                <li>• Encrypted data at rest (AES-256) and in transit (TLS 1.3)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-8 bg-muted/30">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="text-3xl font-light text-foreground mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Download our comprehensive one-pager or explore the demo workflow to see how AthleteNexus streamlines NIL compliance
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate("/demo-workflow")}
              className="h-12 px-8"
            >
              View Demo Workflow
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleDownloadOnePager}
              className="h-12 px-8"
            >
              <Download className="w-4 h-4 mr-2" />
              Download One-Pager
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-8 border-t border-border">
        <div className="max-w-[1200px] mx-auto text-center text-sm text-muted-foreground">
          <p>© 2025 AthleteNexus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default WorkflowDocs;
